import { defineStore } from 'pinia';
import { io, Socket } from 'socket.io-client';
import { handleSessionExpired } from '@/api/axiosInterceptors';
import { useAuthStore } from './authStore';

export type ProjectEvents =
  | 'project:opened'
  | 'project:closed'
  | 'project:updated'
  // Project mail (WI #5643) — emitted by workflows when a mail is ingested, so an
  // open inbox and its unread badge update without a refresh.
  | 'project:mail-arrived';

export type PresenceEvents =
  | 'presence:roll-call'
  | 'presence:announce'
  | 'presence:announced';

export type CursorEvents = 'cursor:move' | 'cursor:moved';

export type SelectionEvents = 'selection:change' | 'selection:changed';

export type ContextbarEvents =
  | 'contextbar:update'
  | 'contextbar:updated'
  | 'contextbar:request'
  | 'contextbar:requested';

export type SocketEvents =
  | ProjectEvents
  | PresenceEvents
  | CursorEvents
  | SelectionEvents
  | ContextbarEvents;

type ReconnectCallback = () => void;

type EventListener = {
  event: SocketEvents;
  callback: (payload: SocketPayload) => void;
};

type SocketStoreState = {
  socketInstance: Socket | null;
  isConnected: boolean;
  _reconnectCallbacks: ReconnectCallback[];
  /**
   * Listeners registered before the socket existed. A route component's `onMounted` runs
   * before WebSocketLayer's, which creates the connection, so on a full page load the
   * subscription would otherwise be lost for good.
   */
  _pendingListeners: EventListener[];
};

export type SocketPayload = Record<string, string | number>;

const logInfo = (message: string) => {
  console.info(`[SOCKET]: ${message}`);
};

const logError = (message: string, error?: unknown) => {
  console.error(`[SOCKET]: ${message}`, error);
};

/**
 * Derive the Socket.IO gateway URL from the API endpoint.
 * VITE_API_ENDPOINT can be a full URL (e.g. "http://localhost:3000/gateway/api")
 * or a relative path (e.g. "/gateway/api" in production).
 * For relative paths, use the current page origin — the gateway is same-origin.
 */
function getSocketUrl(): string {
  const apiEndpoint = import.meta.env.VITE_API_ENDPOINT || '';
  try {
    return new URL(apiEndpoint).origin;
  } catch {
    // Relative path — same origin as the page
    return window.location.origin;
  }
}

export const useSocketStore = defineStore('socketStore', {
  state: (): SocketStoreState => ({
    isConnected: false,
    socketInstance: null,
    _reconnectCallbacks: [],
    _pendingListeners: [],
  }),

  getters: {
    socket: (state) => {
      if (!state.socketInstance) {
        throw new Error('Socket instance is not created yet');
      }

      return state.socketInstance;
    },
  },

  actions: {
    emitEvent(event: ProjectEvents, projectId: string) {
      this.socket.emit(event, { projectId });
    },

    onReconnect(callback: ReconnectCallback) {
      this._reconnectCallbacks.push(callback);
    },

    offReconnect(callback: ReconnectCallback) {
      this._reconnectCallbacks = this._reconnectCallbacks.filter(
        (cb) => cb !== callback,
      );
    },

    /**
     * Subscribe whether or not the socket exists yet. Registering first is normal —
     * children mount before WebSocketLayer — so such listeners are parked and attached by
     * `createSocketConnection` instead of throwing and silently losing the subscription.
     */
    listenEvent(
      event: ProjectEvents,
      callback: (payload: SocketPayload) => void,
    ) {
      if (!this.socketInstance) {
        this._pendingListeners.push({ event, callback });

        return;
      }

      this.socketInstance.on(event, callback);
    },

    /**
     * Counterpart to listenEvent, so handlers don't stack up across navigations. Safe when
     * the socket is already gone — teardown destroys it before children unmount.
     */
    offEvent(event: ProjectEvents, callback: (payload: SocketPayload) => void) {
      this._pendingListeners = this._pendingListeners.filter(
        (listener) =>
          listener.event !== event || listener.callback !== callback,
      );

      this.socketInstance?.off(event, callback);
    },

    createSocketConnection() {
      const authStore = useAuthStore();
      if (!authStore.hasTokens) {
        logError('Cannot create socket — no auth tokens');

        return;
      }

      const socketUrl = getSocketUrl();
      // When running frontend locally against a remote backend, the gateway's
      // Host header (which drives realm resolution for REST) doesn't apply to
      // socket handshakes — the backend reads `app-hostname` from the query
      // instead. `VITE_APP_HOSTNAME_OVERRIDE` lets dev envs point at a real
      // tenant; production falls through to the current window hostname.
      const appHostname =
        import.meta.env.VITE_APP_HOSTNAME_OVERRIDE || window.location.hostname;
      this.socketInstance = io(socketUrl, {
        transports: ['websocket'],
        query: {
          access_token: authStore.tokens.access,
          'app-hostname': appHostname,
        },
      });
      logInfo(`Socket connecting to ${socketUrl}`);

      // Attach anything that subscribed before this ran (see listenEvent).
      this._pendingListeners.forEach(({ event, callback }) =>
        this.socketInstance?.on(event, callback),
      );
      this._pendingListeners = [];

      this.socket.on('connect', () => {
        this.isConnected = true;
        logInfo('Socket connected');
      });

      this.socket.on('disconnect', (reason) => {
        this.isConnected = false;
        logInfo(
          `Socket disconnected, attempting to reconnect... Reason: ${reason}`,
        );
      });

      this.socket.on('connect_error', (error) => {
        this.isConnected = false;
        logError('Socket connection error', error);

        // Backend's socket auth middleware emits errors prefixed with
        // "Authentication failed" when the JWT is invalid/expired. Without
        // this, Socket.IO silently retries forever with the dead token
        // (see work item #5436). Route it through the same session-expired
        // handler as REST 401s: destroy the socket, logout, redirect.
        if (error?.message?.startsWith('Authentication failed')) {
          handleSessionExpired();
        }
      });

      this.socket.io.on('reconnect', (attempt: number) => {
        this.isConnected = true;
        logInfo(`Socket reconnected, attempts: ${attempt}`);
        // Notify all registered callbacks (e.g. ProjectRoot re-joins rooms)
        this._reconnectCallbacks.forEach((cb) => cb());
      });

      this.socket.io.on('reconnect_attempt', () => {
        // Refresh the JWT token in case it expired during disconnect
        const authStore = useAuthStore();
        if (this.socketInstance?.io?.opts?.query) {
          this.socketInstance.io.opts.query['access_token'] =
            authStore.tokens.access;
          logInfo('Socket token refreshed on reconnect attempt');
        }
      });

      this.socket.io.on('reconnect_error', () => {
        this.isConnected = false;
        logError('attempt to reconnect has failed');
      });
    },

    destroySocketConnection() {
      if (this.socketInstance) {
        this.socketInstance.off();
        this.socketInstance.disconnect();
        this.socketInstance = null;
        this.isConnected = false;
        logInfo('Socket connection destroyed');
      }
    },
  },
});
